/**
 * Error categories based on error codes
 */
export enum ErrorCategory {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  SERVICE = 'SERVICE',
  NOT_FOUND = 'NOT_FOUND',
  INTERNAL = 'INTERNAL',
}

/**
 * Domain error interface
 */
export interface DomainError {
  code: string;
  message: string;
  userMessage: string;
  category: ErrorCategory;
  details?: unknown;
  correlationId?: string;
}

/**
 * Gets a user-friendly message based on error category
 */

export function getUserFriendlyMessage(category: ErrorCategory, message: string, code?: string): string {
  switch (category) {
    case ErrorCategory.AUTHENTICATION:
      return 'Please sign in to continue';
    case ErrorCategory.AUTHORIZATION:
      return 'You do not have permission to perform this action';
    case ErrorCategory.VALIDATION:
      return `Invalid input: ${message}`;
    case ErrorCategory.SERVICE:
      return getUserFriendlyServiceErrorMessage(message, code);
    case ErrorCategory.NOT_FOUND:
      return 'The requested resource was not found';
    case ErrorCategory.INTERNAL:
    default:
      return 'An unexpected error occurred';
  }
}

function getUserFriendlyServiceErrorMessage(message: string, code?: string): string {
  if (code && code.includes('_BAD_REQUEST')) {
    return message;
  }

  if (code && code.includes('_CONFLICT')) {
    return message; // Preserve specific conflict messages from backend
  }

  if (code && code.includes('_UNAVAILABLE')) {
    return 'The service is currently unavailable';
  }

  if (code && code.includes('_TIMEOUT')) {
    return 'The request to the service timed out';
  }

  return 'The service is temporarily unavailable';
}

/**
 * Maps error codes to categories
 */
export function mapErrorCodeToCategory(code: string): ErrorCategory {
  if (code === 'UNAUTHENTICATED' || code.startsWith('AUTH_')) {
    return ErrorCategory.AUTHENTICATION;
  }

  if (code.startsWith('FORBID_')) {
    return ErrorCategory.AUTHORIZATION;
  }

  if (code.startsWith('VALID_')) {
    return ErrorCategory.VALIDATION;
  }

  if (code.startsWith('SVC_')) {
    return ErrorCategory.SERVICE;
  }

  if (code.startsWith('NOT_FOUND_')) {
    return ErrorCategory.NOT_FOUND;
  }

  return ErrorCategory.INTERNAL;
}
